import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {TeamLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {snackbarAtom} from 'atoms/snackbar';
import {
  Employee,
  EmployeeWithHierarchy,
  ProfilePage,
  TeamAssociation,
  TeamWithCountsNew,
} from 'client';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Layout} from 'components/DSYS/Layout';
import {SavableTeamListTypeahead} from 'components/DSYS/TeamListTypeahead';
import {Title} from 'components/DSYS/Title';
import {AboutMe} from 'components/profile/AboutMe';
import {MyWork} from 'components/profile/MyWork';
import {ProfileBody} from 'components/profile/ProfileBody';
import {ProfileEmployees} from 'components/profile/ProfileEmployees';
import {recordOpenAtom} from 'components/search/box/frequentPeople';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useEditTeams} from 'hooks/useEmployee';
import {useProfile} from 'hooks/useProfile';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {NotFound} from 'pages/NotFound';
import {OrgChart} from 'pages/OrgChart/Chart';
import {FocusData} from 'pages/OrgChart/helpers';
import {useEffect, useState} from 'react';
import {useLoaderData, useLocation} from 'react-router';

const reportingLineToTree = (reportingLine: Employee[]) => {
  let tree: EmployeeWithHierarchy = {...reportingLine[0], children: []};

  for (const employee of reportingLine.slice(1)) {
    tree = {
      ...employee,
      children: [tree],
    };
  }

  return tree;
};

export const ProfileView = () => {
  const routeProfile = useLoaderData() as ProfilePage | null;
  const {data: queryProfile} = useProfile({
    ldap: routeProfile?.employee.ldap,
    throwOnError: false,
  });
  const props = queryProfile ?? routeProfile;
  const employeeLdap = props?.employee?.ldap ?? '';

  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [focus, setFocus] = useState<FocusData | undefined>(() => routeProfile?.employee);

  const {editTeams, isPending} = useEditTeams({id: employeeLdap, ldap: employeeLdap});

  const [showEditTeams, setShowEditTeams] = useState(false);

  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const recordOpen = useSetAtom(recordOpenAtom);
  const {isAskRelaunchEnabled} = useAtomValue(growthbookCacheAtom);

  useDocumentTitle(props?.employee?.name ?? '');

  useEffect(() => {
    if (!props?.employee) return;
    setFocus(props.employee);
    setExpanded(false);
  }, [props?.employee]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!props?.employee) return;
    analyticsLogger().logEvent('PROFILE_OPENED', {
      level: props.employee.level ?? undefined,
      source: location.state?.source,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.employee]);

  // Once per profile landed on (not per refetch), for search's Frequent people.
  useEffect(() => {
    if (props?.employee) recordOpen(props.employee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeLdap]);

  if (!props?.employee) {
    return <NotFound />;
  }

  const directReportLdaps = props.directReports.map((employee) => employee.ldap);
  const peers = (props.teamMembers || []).filter((teamMember) => {
    const isManager = teamMember.user_id === props.employee.manager_id;
    const isDirectReport = directReportLdaps.indexOf(teamMember.ldap) !== -1;
    // Peer cannot be user's direct report or manager
    return !isManager && !isDirectReport;
  });

  // To avoid confusion after RIFs, hide breadcrumbs, reporting lines, reports, and peers
  // on deactivated employees' profiles.
  // EXCEPT: Keep breadcrumbs, reporting lines, reports, and peers visible when manager has
  // active direct reports, so their reports remain reachable in profiles, teams and org chart.
  const activeDirectReports = props.directReports.filter((employee) => !employee.is_deactivated);
  const shouldHideProfileSections =
    props.employee.is_deactivated && activeDirectReports.length === 0;

  // const profileActionLog = (action: string) =>
  //   analyticsLogger().logEvent('PROFILE_INTERACTION', {action});

  const onCancel = () => {
    setShowEditTeams(false);
  };

  const onSave = async (selections: TeamAssociation[]) => {
    try {
      await editTeams({
        teams: selections.map((assoc) => ({
          team_id: assoc.team.team_id,
          slug: assoc.team.slug ?? '',
          allocation: assoc.allocation,
        })),
      });

      setSnackbarMessage({text: t('saved')});
      setShowEditTeams(false);
    } catch {
      setSnackbarMessage({text: t('error_saving')});
    }
  };

  const onExpandChart = (expand: boolean) => {
    setShowEditTeams(false);
    setExpanded(expand);
  };

  return (
    <Layout.InlineDrawerContainer
      width={expanded ? '100%' : 'default'}
      open={showEditTeams}
      breadcrumb={
        shouldHideProfileSections
          ? undefined
          : expanded
            ? []
            : [
                {children: t('people'), to: '/people'},
                ...(props.reportingLine ?? [])
                  .map(({name, ldap}) => ({
                    key: ldap,
                    children: (
                      <PersonHoverCard ldap={ldap} name={name}>
                        <span>{name}</span>
                      </PersonHoverCard>
                    ),
                    to: `/people/${ldap}`,
                  }))
                  .reverse(),
              ]
      }
      drawerHeader={<Title size={18}>{t('edit_teams')}</Title>}
      drawerIcon={TeamLine}
      drawerBody={
        <SavableTeamListTypeahead
          isPending={isPending}
          employee={props.employee}
          memberSort={(a, b) => (a.team.name ?? '').localeCompare(b.team.name ?? '')}
          onSave={onSave}
          onCancel={onCancel}
          withRightAccessory={(assoc) => <Text>{assoc.allocation}%</Text>}
        />
      }
      onClose={() => setShowEditTeams(false)}
    >
      <Stack gap="24">
        <Box
          opacity={expanded ? 0 : 1}
          paddingBottom="8"
          style={{
            transition: 'opacity var(--easing__linear) var(--duration__non-motion)',
          }}
        >
          <ProfileBody
            publicAccountId={props.public_account_id}
            employee={props.employee}
            employeeTeams={props.employeeTeams}
            eventSubscriptions={props.eventSubscriptions}
            showPto={props.showPto}
            admin={props.admin}
            supportedLeaders={props.supportedLeaders}
            onEditTeams={() => setShowEditTeams(true)}
            showAsk={isAskRelaunchEnabled}
            reportingLine={props.reportingLine}
          />
        </Box>

        {!shouldHideProfileSections && props.reportingLine.length > 1 && (
          <OrgChart
            overrideTree={reportingLineToTree(props.reportingLine)}
            toggle={{
              employee: props.employee,
              team: getTeamWithMostMembers(props.employeeTeams),
            }}
            focus={focus ?? props.employee}
            setFocus={setFocus}
            expanded={expanded}
            setExpanded={onExpandChart}
          />
        )}

        <Box opacity={expanded ? 0 : 1}>
          {!shouldHideProfileSections && props.directReports && props.directReports.length > 0 && (
            <ProfileEmployees
              sectionTitle={t('reports')}
              employees={props.directReports}
              employee={props.employee}
              source="reports"
              collapsedLimit={24}
            />
          )}
        </Box>

        <Stack className={atoms({opacity: expanded ? 0 : 1})} gap="24">
          {!shouldHideProfileSections && peers.length > 0 && (
            <ProfileEmployees
              sectionTitle={t('peers')}
              employees={peers}
              employee={props.employee}
              source="peers"
            />
          )}

          {!shouldHideProfileSections && (
            <MyWork employee={props.employee} employeeTeams={props.employeeTeams} />
          )}

          {!props.employee.is_deactivated && (
            <AboutMe employee={props.employee} workingWithMe={props.workingWithMe} />
          )}
        </Stack>
      </Stack>
    </Layout.InlineDrawerContainer>
  );
};

const getTeamWithMostMembers = (teams: TeamWithCountsNew[]): TeamWithCountsNew | undefined => {
  return teams.length === 0
    ? undefined
    : teams.reduce((maxTeam, currentTeam) =>
        currentTeam.total_employee_count > maxTeam.total_employee_count ? currentTeam : maxTeam
      );
};
